import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Lock, LogOut, Menu, X } from 'lucide-react';
import { useInstance } from '../contexts/InstanceContext';
import { withBackendPath } from '../config/api';
import { clearPinSession } from '../utils/pinSession';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { usePublishTopChromeHeight } from '../hooks/usePublishTopChromeHeight.js';
import { buildMenuItems, findMenuItemForPath } from '../utils/participantEntry';
import useInstanceGrant from '../hooks/useInstanceGrant';
import { gradientText } from '../styles/mixins';
import HeaderPhaseMenu from './HeaderPhaseMenu';
import HeaderDeviceMenu, { UserMenu, UserDropdown, MenuButton, MenuItems } from './HeaderDeviceMenu';

const Header = styled.div`
  position: ${props => (props.$isStorybook || props.$inDocumentFlow) ? 'relative' : 'absolute'};
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 24px;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* The title opens the phase menu too, as the logo does (no hover state: touch first) */
const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  position: relative;
  min-height: 44px;
  cursor: ${(p) => (p.$opens ? 'pointer' : 'default')};
  -webkit-tap-highlight-color: transparent;
  &:focus-visible { outline: 2px solid #5B9DD9; outline-offset: 2px; border-radius: 6px; }
`;

const MainTitle = styled.h1`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  ${gradientText('#4CAF50', '#2196F3', '#4CAF50')}
  line-height: 1.2;
`;

const InstanceTitle = styled.span`
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.2;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LockIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(244, 67, 54, 0.15);
  color: #EF5350;
  border: 1px solid rgba(244, 67, 54, 0.25);
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
`;

// Cluster wrapping every right-side element (inline actions, hamburger
// button, avatar). margin-left:auto keeps the whole cluster pinned to
// the top-right corner regardless of how many items it contains — without
// this, Header's space-between would push a lone avatar to the centre.
const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
`;

const RightSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;

  /* Mobile: hide right-section actions behind a hamburger. When the
   * menu opens, RightSection slides out of the header into a full-width
   * panel beneath it. Children stack vertically so they get readable
   * touch targets. */
  @media (max-width: 700px) {
    display: ${p => p.$mobileOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px 16px;
    background: rgba(0, 0, 0, 0.92);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

const Hamburger = styled.button`
  display: none;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(255, 255, 255, 0.08); }

  @media (max-width: 700px) {
    display: inline-flex;
  }
`;


const Avatar = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #4CAF50;
  background: rgba(59, 130, 246, 0.3);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.5);
  transition: box-shadow 0.15s, transform 0.15s;
  &:hover { box-shadow: 0 0 14px rgba(76, 175, 80, 0.8); transform: scale(1.05); }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;


const UserInfo = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 4px;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.5);
  margin-top: 2px;
`;

const UserRole = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(59,130,246,0.2);
  color: #93c5fd;
  margin-top: 6px;
  display: inline-block;
`;



// Title when the chain has no menu entry for this screen (not loaded yet, or a
// module opened by its direct URL): module family → navigation.* key.
const FALLBACK_TITLE_KEYS = {
  impact_path: 'navigation.impactPath',
  systemic_map: 'navigation.systemicMap',
  pain_points_collect: 'navigation.painPoints',
  pivot_points: 'navigation.pivotPoints',
  kuusi_kenttaa: 'kuusiKenttaa.title',
  vipupiste_focus: 'vipupisteFocus.title',
  ongelmat: 'navigation.v2Ongelmat',
  ankkurit: 'navigation.v2Ankkurit',
  juurisyyt: 'navigation.v2Juurisyyt',
  lenses: 'navigation.lenses',
  solutions: 'navigation.solutions',
  report: 'navigation.report',
};

/**
 * MainViewHeader - Shared header for main views (Impact Path, Pivot Points, SWOT)
 *
 * Features:
 * - The Kuutio logo opens the workshop's phases (HeaderPhaseMenu): the instance's
 *   participant modules in chain order; a module stays locked until the flow
 *   reaches it or the facilitator opens it (#685). The view title opens it too.
 * - Instance subtitle
 * - Right section for view-specific controls
 * - Status dot (green/red) for settings access
 */
export default function MainViewHeader({
  activeView = null, // module family of this screen: 'impact_path' | 'pivot_points' | 'lenses' | …
  instanceTitle = '',
  subtitleExtra = null, // ReactNode shown next to instanceTitle (e.g. perspective badge)
  centerContent = null, // ReactNode rendered between left/right with vertical divider
  children, // Right side content (controls)
  isStorybook = false,
  inDocumentFlow = false, // relative header so parent flex layout can size content below
  onSettingsClick = null, // Callback when status dot is clicked
  status = 'ok', // 'ok' | 'error' - controls dot color
  hideStatusDot = false, // Suppress the connection-status dot entirely. The
                         // public report uses this when it wants the sign-in
                         // button to occupy that slot without a competing dot.
  disableNavigation = false, // Disable cube click & view navigation
  onViewChange = null, // Alternative to navigate() for embedded/screen mode
  titleOverride = null, // When set, render this string as plain title (no dropdown). Used on /instances.
  deviceMenuItems = null, // a screen's own entries for the dot's menu (and the avatar's): [{ key, label, icon, onClick }]
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentInstance, participantChain, participantChainError } = useInstance();
  const { t, language } = useTranslation();

  // Locks are the room's: whoever may facilitate this Kuutio (admins, owners, a
  // facilitator with a grant) sees every entry open (29.10.2026).
  const seesAll = useInstanceGrant(currentInstance?.id, 'facilitate');
  const menuItems = useMemo(() => {
    const items = buildMenuItems(participantChain, language);
    return seesAll ? items.map((m) => ({ ...m, unlocked: true })) : items;
  }, [participantChain, language, seesAll]);
  // A wall screen (onViewChange) stays on /screen, so it is matched by family
  const currentItem = (!onViewChange && findMenuItemForPath(menuItems, location.pathname))
    || menuItems.find((m) => m.family === activeView)
    || null;
  const currentTitle = currentItem?.label
    || (FALLBACK_TITLE_KEYS[activeView] ? t(FALLBACK_TITLE_KEYS[activeView]) : 'Kuutio');
  const [isViewSelectorOpen, setIsViewSelectorOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // Mobile hamburger — collapses the RightSection (Kirjaudu, edit, print,
  // user menu) behind a single button below 700px. Tap toggles a slide-down
  // panel; tapping anywhere outside closes it.
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const [authUser, setAuthUser] = useState(null);
  const phaseMenuRef = useRef(null); // the logo and its phase menu
  const userMenuRef = useRef(null);
  const headerRef = useRef(null);

  usePublishTopChromeHeight(headerRef);

  // Fetch current user. Always — the session cookie is httpOnly so JS can't
  // peek at document.cookie to short-circuit. /auth/me is cheap (~20ms) and
  // returns 401 when there's no session, so the failure case is also fast.
  useEffect(() => {
    fetch(withBackendPath('/auth/me'), { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(user => { if (user) setAuthUser(user); })
      .catch(() => {});
  }, []);

  // Close dropdowns on a press outside them. A pointer press, caught on its way down: the maps'
  // pan-and-zoom (d3-zoom) stops a mousedown before it reaches the document, and on an iPad a
  // tap on the map makes no mousedown at all, so a menu left open over a map never closed.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (phaseMenuRef.current && !phaseMenuRef.current.contains(event.target)) {
        setIsViewSelectorOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Don't close if the tap landed on the hamburger button — its
        // onClick toggles state and we'd race against it.
        const onHamburger = event.target.closest?.('button[aria-label="Avaa valikko"], button[aria-label="Sulje valikko"]');
        if (!onHamburger) setIsMobileMenuOpen(false);
      }
    };

    if (isViewSelectorOpen || isUserMenuOpen || isMobileMenuOpen) {
      document.addEventListener('pointerdown', handleClickOutside, true);
      return () => document.removeEventListener('pointerdown', handleClickOutside, true);
    }
  }, [isViewSelectorOpen, isUserMenuOpen, isMobileMenuOpen]);

  const handleSignOut = async () => {
    await fetch(withBackendPath('/auth/logout'), { method: 'POST', credentials: 'include' }).catch(() => {});
    // The server ended this device's PIN session too (#1480); drop its marker with it.
    clearPinSession();
    setAuthUser(null);
    setIsUserMenuOpen(false);
    // Land on the public landing page — the 'Olen Sitralainen' button is the
    // single sign-in entry point. /auth/login is a legacy backend PIN page.
    window.location.href = '/';
  };




  // Close on escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsViewSelectorOpen(false);
    };

    if (isViewSelectorOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isViewSelectorOpen]);

  // A wall screen can show only modules that have a wall view
  const isSelectable = (item) => item.unlocked && (!onViewChange || Boolean(item.wallView));

  const handleViewSelect = (item) => {
    setIsViewSelectorOpen(false);
    if (item === currentItem) return;
    if (onViewChange) {
      onViewChange(item.wallView);
      return;
    }
    if (!disableNavigation) {
      const params = new URLSearchParams(window.location.search);
      // A phase chosen from the menu always opens on its intro (useModuleIntro); a screen without one ignores it
      params.set('intro', '1');
      navigate(`${item.route}?${params.toString()}`);
    }
  };

  return (
    <Header ref={headerRef} $isStorybook={isStorybook} $inDocumentFlow={inDocumentFlow}>
      {/* The Kuutio logo and the title both open the workshop's phases (#1326). The two
          are one area for the outside-tap check, so a tap on the title toggles the menu
          rather than closing it and opening it again. */}
      <LeftSection ref={phaseMenuRef}>
        <HeaderPhaseMenu
          open={isViewSelectorOpen}
          disabled={Boolean(titleOverride)}
          items={menuItems}
          currentItem={currentItem}
          isSelectable={isSelectable}
          error={participantChainError}
          onToggle={() => setIsViewSelectorOpen((o) => !o)}
          onSelect={handleViewSelect}
          t={t}
        />
        <TitleSection
          $opens={!titleOverride}
          {...(titleOverride ? {} : {
            role: 'button',
            tabIndex: 0,
            'aria-expanded': isViewSelectorOpen,
            'aria-haspopup': 'menu',
            onClick: () => setIsViewSelectorOpen((o) => !o),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsViewSelectorOpen((o) => !o); }
            },
          })}
        >
          <MainTitle>{titleOverride || currentTitle}</MainTitle>
          {(instanceTitle || subtitleExtra || currentInstance?.settings?.locked) && (
            <SubtitleRow>
              {instanceTitle && <InstanceTitle>{instanceTitle}</InstanceTitle>}
              {subtitleExtra}
              {currentInstance?.settings?.locked && (
                <LockIndicator>
                  <Lock size={10} />
                  {t('navigation.locked')}
                </LockIndicator>
              )}
            </SubtitleRow>
          )}
        </TitleSection>
      </LeftSection>

      {centerContent && (
        <CenterSection>{centerContent}</CenterSection>
      )}

      <RightCluster>
        {/* Order on desktop: [actions] [avatar]. Actions live in
          * RightSection which is inline on desktop and slide-down panel
          * on mobile. The hamburger sits between them so on mobile the
          * tap-target is right next to the avatar. */}
        <RightSection $mobileOpen={isMobileMenuOpen} ref={mobileMenuRef}>
          {children}
        {/* Status dot only visible when *not* signed in — once we have an authUser
            the green-ringed avatar conveys the same "connected" signal more usefully. */}
        {!authUser && !hideStatusDot && (
          <HeaderDeviceMenu status={status} onSettingsClick={onSettingsClick} items={deviceMenuItems} />
        )}
        </RightSection>

        {/* Hamburger only shows when there's something to collapse.
         * Below 700px RightSection becomes a slide-down panel; the
         * button toggles it. Hidden on desktop via CSS. */}
        {!!children && (
          <Hamburger
            type="button"
            onClick={() => setIsMobileMenuOpen(o => !o)}
            aria-label={isMobileMenuOpen ? 'Sulje valikko' : 'Avaa valikko'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </Hamburger>
        )}

        {/* Avatar — sits at the far right of the top bar at every
         * viewport. Dropdown holds only user info + sign-out; the
         * inline actions (Muokkaa/Tulosta) live in RightSection to its
         * left. */}
        {authUser && (
          <UserMenu ref={userMenuRef} style={{ flexShrink: 0 }}>
            <Avatar onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} title={authUser.name || authUser.email}>
              {authUser.photo_url
                ? <img src={authUser.photo_url} alt={authUser.name || authUser.email} />
                : (authUser.name || authUser.email || '?')[0].toUpperCase()}
            </Avatar>
            {isUserMenuOpen && (
              <UserDropdown>
                <UserInfo>
                  {authUser.name && <UserName>{authUser.name}</UserName>}
                  <UserEmail>{authUser.email}</UserEmail>
                  <UserRole>{authUser.role}</UserRole>
                </UserInfo>
                <MenuItems items={deviceMenuItems} onPick={() => setIsUserMenuOpen(false)} />
                <MenuButton onClick={handleSignOut}>
                  <LogOut size={14} /> {t('navigation.signOutShort')}
                </MenuButton>
              </UserDropdown>
            )}
          </UserMenu>
        )}
      </RightCluster>
    </Header>
  );
}
